import { createApp, ref } from 'vue';
import { configureGoogleMaps } from '@/utils/google-maps-loader';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import '@mdi/font/css/materialdesignicons.css';
// The app's brand font (matches the landing page); Vuetify is pointed at it via
// app/frontend/styles/vuetify-settings.scss. Without loading real font files the browser
// would silently fall back to Helvetica.
import '@fontsource/figtree/300.css';
import '@fontsource/figtree/400.css';
import '@fontsource/figtree/500.css';
import '@fontsource/figtree/600.css';
import '@fontsource/figtree/700.css';
import 'vuetify/styles';
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';
import { createI18n } from 'vue-i18n';
import { createKuidoVuetify } from '../plugins/vuetify';
import { initNewRelicBrowserAgent, attachVueErrorHandler } from '../utils/new-relic';
import { useSiteVariables } from '../composables/useSiteVariables';
import type { SerializedUser } from '../api/users';
import { Locales } from '../locales/locales';
import App from '../components/app.vue';
import TheNavbar from '../components/the-navbar.vue';
import CalendarView from '../components/calendar/calendar-view.vue';
import CaregiverForm from '../components/caregivers/caregiver-form.vue';
import CaregiversList from '../components/caregivers/caregivers-list.vue';
import CaregiverProfile from '../components/caregivers/caregiver-profile.vue';
import ChargesList from '../components/charges/charges-list.vue';
import ChargeClosuresList from '../components/charge-closures/charge-closures-list.vue';
import DashboardView from '../components/dashboard/dashboard-view.vue';
import EditPasswordForm from '../components/passwords/edit-password-form.vue';
import HealthStatusesView from '../components/health-statuses/health-statuses-view.vue';
import InventoryView from '../components/inventory/inventory-view.vue';
import ErpIntegrationView from '../components/erp-integration/erp-integration-view.vue';
import PatientForm from '../components/patients/patient-form.vue';
import PatientsList from '../components/patients/patients-list.vue';
import PatientProfile from '../components/patients/patient-profile.vue';
import PatientShifts from '../components/patients/patient-shifts.vue';
import PatientVitalSigns from '../components/patients/patient-vital-signs.vue';
import PaymentsList from '../components/payments/payments-list.vue';
import RecoverPasswordForm from '../components/passwords/recover-password-form.vue';
import SettingsView from '../components/settings/settings-view.vue';
import RoutePlansList from '../components/route-plans/route-plans-list.vue';
import RoutePlanDetail from '../components/route-plans/route-plan-detail.vue';
import MonthlyTrackingPage from '../components/monthly-tracking/monthly-tracking-page.vue';
import CoverageAlertsPage from '../components/coverage-alerts/coverage-alerts-page.vue';
import PlanningPage from '../components/planning/planning-page.vue';
import ShiftBatchCreateForm from '../components/shifts/shift-batch-create-form.vue';
import ShiftImportForm from '../components/shifts/shift-import-form.vue';
import ShiftImportDetail from '../components/shifts/shift-import-detail.vue';
import ShiftsList from '../components/shifts/shifts-list.vue';
import ShiftsPage from '../components/shifts/shifts-page.vue';
import ShiftsTodayPage from '../components/shifts/shifts-today-page.vue';
import UserEditPasswordForm from '../components/users/user-edit-password-form.vue';
import UserLoginForm from '../components/users/user-login-form.vue';
import UserRecoverPasswordForm from '../components/users/user-recover-password-form.vue';
import UserSession from '../components/users/user-session.vue';
import '../css/application.css';

initNewRelicBrowserAgent();

export const i18n = createI18n({
  legacy: false,
  locale: 'es',
  fallbackLocale: 'es',
  messages: Locales.messages,
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  },
};

const vuetify = createKuidoVuetify();

// eslint-disable-next-line max-statements
document.addEventListener('DOMContentLoaded', () => {
  const app = createApp(App);
  attachVueErrorHandler(app);

  app.component('CalendarView', CalendarView);
  app.component('CaregiverForm', CaregiverForm);
  app.component('CaregiversList', CaregiversList);
  app.component('CaregiverProfile', CaregiverProfile);
  app.component('ChargesList', ChargesList);
  app.component('ChargeClosuresList', ChargeClosuresList);
  app.component('DashboardView', DashboardView);
  app.component('EditPasswordForm', EditPasswordForm);
  app.component('HealthStatusesView', HealthStatusesView);
  app.component('InventoryView', InventoryView);
  app.component('ErpIntegrationView', ErpIntegrationView);
  app.component('PatientForm', PatientForm);
  app.component('PatientsList', PatientsList);
  app.component('PatientProfile', PatientProfile);
  app.component('PatientShifts', PatientShifts);
  app.component('PatientVitalSigns', PatientVitalSigns);
  app.component('PaymentsList', PaymentsList);
  app.component('RecoverPasswordForm', RecoverPasswordForm);
  app.component('RoutePlansList', RoutePlansList);
  app.component('RoutePlanDetail', RoutePlanDetail);
  app.component('MonthlyTrackingPage', MonthlyTrackingPage);
  app.component('CoverageAlertsPage', CoverageAlertsPage);
  app.component('PlanningPage', PlanningPage);
  app.component('SettingsView', SettingsView);
  app.component('ShiftBatchCreateForm', ShiftBatchCreateForm);
  app.component('ShiftImportForm', ShiftImportForm);
  app.component('ShiftImportDetail', ShiftImportDetail);
  app.component('ShiftsList', ShiftsList);
  app.component('ShiftsPage', ShiftsPage);
  app.component('ShiftsTodayPage', ShiftsTodayPage);
  app.component('TheNavbar', TheNavbar);
  app.component('UserEditPasswordForm', UserEditPasswordForm);
  app.component('UserLoginForm', UserLoginForm);
  app.component('UserRecoverPasswordForm', UserRecoverPasswordForm);
  app.component('UserSession', UserSession);

  const siteVariables = useSiteVariables();
  const currentUser = ref<SerializedUser | undefined>(siteVariables?.currentUser);
  app.provide('currentUser', currentUser);
  configureGoogleMaps({ region: currentUser.value?.companyCountryCode });
  app.provide('clearCurrentUser', () => {
    currentUser.value = undefined;
  });
  app.provide('setCurrentUser', (user: SerializedUser) => {
    currentUser.value = user;
  });

  app.use(i18n);
  app.use(pinia);
  app.use(VueQueryPlugin, vueQueryPluginOptions);
  app.use(vuetify);

  app.mount('#vue-app');

  return app;
});
